import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PublicShell from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { CalendarIcon as Calendar, MapPinIcon as MapPin, CurrencyDollarIcon as CurrencyDollar } from "@phosphor-icons/react";
import { formatDate, publicFileUrl } from "@/lib/utils-app";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  show: "Show",
  sport: "Sport",
  health_clinic: "Health clinic",
  meetup: "Meetup",
  other: "Other",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Public endpoint — no auth needed
    axios.get(`${API}/events`)
      .then((r) => setEvents(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicShell>
      <div className="mb-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Events</p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Upcoming shows, sports & meetups</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Conformation shows, sport competitions, health clinics, and breed meetups. Sign up your dog to participate.
        </p>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-16 text-center">
          <Calendar size={48} weight="duotone" className="text-ink mx-auto mb-4" />
          <h3 className="font-spectral text-2xl mb-2">No events scheduled</h3>
          <p className="text-[#6B6B6B] mb-5">
            Shows, meetups, and competitions will appear here. In the meantime,
            the community is active every day on The Dog&apos;s Voice.
          </p>
          <Link
            to="/feed"
            className="inline-flex items-center rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 h-10 transition-colors"
            data-testid="events-empty-feed-cta"
          >
            Visit The Dog&apos;s Voice →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e) => (
            <Link
              key={e.id}
              to={`/events/${e.id}`}
              className="group bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-[2px] transition-transform"
              data-testid={`event-card-${e.id}`}
            >
              <div className="aspect-[16/9] bg-[#0E0E0E] relative overflow-hidden">
                {e.cover_image ? (
                  <img
                    src={publicFileUrl(e.cover_image)}
                    alt={e.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0E0E0E] via-[#1a1a1a] to-[#C1272D]/40" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-white/95 backdrop-blur rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.15em]">
                    {TYPE_LABELS[e.event_type] || "Event"}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="font-spectral text-2xl tracking-tight drop-shadow">{e.title}</h3>
                </div>
              </div>
              <div className="p-5 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#6B6B6B]">
                  <Calendar size={14} />
                  {formatDate(e.start_date)}
                  {e.end_date && e.end_date !== e.start_date && ` — ${formatDate(e.end_date)}`}
                </div>
                <div className="flex items-center gap-2 text-[#6B6B6B]">
                  <MapPin size={14} />
                  {e.location}
                </div>
                <div className="flex items-center gap-2 text-ink font-semibold">
                  <CurrencyDollar size={14} />
                  {e.entry_fee > 0 ? `$${e.entry_fee.toFixed(2)} entry` : "Free entry"}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PublicShell>
  );
}
